import { useCallback, useRef, useState } from 'react'
import { Button, Input, Space, Spin, Typography, theme, Tag } from 'antd'
import type { InputRef } from 'antd'
import { SendOutlined, StarOutlined } from '@ant-design/icons'
import { BacktestChat } from '@/api'
import { createUuid } from '@/utils/uuid'
import type { Msg , StartBacktestParams, ChatBacktestResponse } from '@/types'
import '@/styles/aiChat.scss'

const { Text } = Typography

type Msge = Msg & {
    context?: StartBacktestParams
    shortcutType?: number
    suggested_strategy_snapshot?: string
    suggested_params?: ChatBacktestResponse['suggested_params']
}

export default function AIChat({ backtestFormData ,step = 0,setBacktestFormData}: { backtestFormData: StartBacktestParams ,step: number,setBacktestFormData: (prev: StartBacktestParams) => void}) {
    const allShortcutTypeList = [
        { label: '优化策略', value: 0, msg: '优化策略' },
        { label: '分析当前策略优缺点', value: 1, msg: '分析当前策略的优势和潜在风险' },
        { label: '回测参数建议', value: 2, msg: '建议合适的回测区间和K线周期' },
        { label: '适用市场环境', value: 3, msg: '这个策略适合哪些市场环境' },
    ]
    const shortcutTypeList = step != 0
        ? allShortcutTypeList.filter((item) => item.value !== 1 && item.value !== 0)
        : allShortcutTypeList.filter((item) => item.value === 1 || item.value === 0)
    const { token } = theme.useToken()
    const [time] = useState(new Date().toLocaleTimeString())
    const [sessionId] = useState(() => createUuid())
    const [messages, setMessages] = useState<Msge[]>([])
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(false)
    const chatBodyRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<InputRef>(null)

    const scrollBottom = () =>
        setTimeout(() => {
            const chatBody = chatBodyRef.current
            if (!chatBody) return
            chatBody.scrollTo({
                top: chatBody.scrollHeight,
                behavior: 'smooth',
            })
        }, 50)

    const send = useCallback(async (msg: string, shortcutType?: number) => {
        if (!msg || loading) return
        const userMsg: Msge = { session_id: sessionId, role: 'user', message: msg, time: new Date().toLocaleTimeString() }
        setMessages((prev) => [...prev, userMsg])
        setInput('')
        setLoading(true)
        try {
            const { response, response_html,suggested_strategy_snapshot ,suggested_params } = await BacktestChat({
                session_id: sessionId,
                message: msg,
                context: backtestFormData,
            })
            setMessages((prev) => [
                ...prev,
                { 
                    session_id: sessionId, 
                    role: 'assistant', 
                    message: response_html || response, 
                    time: new Date().toLocaleTimeString() ,
                    suggested_strategy_snapshot: suggested_strategy_snapshot,
                    suggested_params: suggested_params,
                    shortcutType: shortcutType,
                },
            ])
            scrollBottom()
        } finally {
            setLoading(false)
            setTimeout(() => inputRef.current?.focus(), 0)
        }
    }, [input, loading, backtestFormData])

    const handleClear = () => {
        !loading && setMessages([])
    }

    const handleShortcut = (item: { label: string, value: number , msg: string }) => {
        send(item.msg, item.value)
    }

    const handleStrategyWriteBack = (msg: Msge) => {
        if(msg.shortcutType === 0) {
            setBacktestFormData({
                ...backtestFormData,
                strategy_snapshot: msg.suggested_strategy_snapshot || '',
            })
        } else if(msg.shortcutType === 2) {
            setBacktestFormData({
                ...backtestFormData,
                ...(msg.suggested_params)
            })
        }
    }

    return (
        <div className='ai-chat-container'>
            {/* Header */}
            <div
                className='ai-chat-header'
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><StarOutlined style={{ color: '#3b82f6' }} />策略调试助手<div className='ai-dot'></div></div>
                <Tag style={{ fontSize: 10, cursor: 'pointer' }} onClick={handleClear}>清空</Tag>
            </div>

            {/* Chat area */}
            <div className='ai-chat-body' ref={chatBodyRef}>

                <div className='ai-message ai-chat-alert-messages'>
                        <div className='ai-chat-alert-messages-title'>
                        {
                            !backtestFormData.manager_id ? '请先选择一位组合经理，系统将加载其策略快照供你调试。' : 
                            <>
                                `已加载【{backtestFormData.version_no}·{backtestFormData.version_name}】策略描述：<br/><br/>
                                <div>{backtestFormData.strategy_snapshot}</div><br/>
                                可告诉我如何调整这个策略，AI 将生成优化后的自然语言策略描述，并更新至左侧预览
                            </>
                        } 
                    </div>
                    <div className='ai-message-time'>{time}</div>
                </div>

                {messages.map((msg, index) => (
                    <div
                        key={index}
                        className={`ai-message ${msg.role === 'user' ? 'ai-message-user' : 'ai-message-assistant'}`}
                    >
                        {
                            msg.role === 'user' ? msg.message : <div dangerouslySetInnerHTML={{ __html: msg.message }} />
                        }
                        {
                             msg.role === 'assistant' && (msg.shortcutType === 0 || msg.shortcutType === 2) && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button type='link' className='ai-message-strategy-write-back' onClick={() => handleStrategyWriteBack(msg)}>采纳</Button>
                                </div>
                            )
                        }
                        <div className='ai-message-time'>{msg.time}</div>
                    </div>
                ))}

                {loading && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 12 }}>
                        <div style={{ padding: '8px 12px', background: token.colorFillSecondary, borderRadius: 8 }}>
                            <Spin size="small" />
                        </div>
                    </div>
                )}
                {
                    backtestFormData.manager_id && (
                        <div className='ai-chat-shortcut'>
                            <div className='ai-chat-shortcut-btn'>
                                {
                                    shortcutTypeList.map((item) => (

                                        <Text key={item.value} className='text-12' style={{ marginRight: 6, cursor: 'pointer' }} onClick={() => handleShortcut(item)}>{item.label}</Text>
                                    ))
                                }
                            </div>
                        </div>
                    )
                }
            </div>

            {/* Input bar */}
            <div className='ai-input-bar'>
                <Space.Compact style={{ width: '100%' }}>
                    <Input
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onPressEnter={() => send(input)}
                        placeholder="输入分析指令或问题"
                        disabled={loading}
                    />
                    <Button type="primary" size="large" style={{ height: '36px' }} icon={<SendOutlined />} onClick={() => send(input)} loading={loading} />
                </Space.Compact>
            </div>
        </div>
    )
}
